/**
 * apps/battle/client/src/terminal/screens/LeaderboardScreen.tsx
 *
 * Phase 4.2 M10 — global leaderboard (opened from TitleScreen with [V]).
 *
 *   GET /api/leaderboard → { enabled, entries[], self }
 *
 * Renders a RANK·NAME·LVL·XP·BADGES table for the top players, highlights the
 * current player's row, and shows a "MY BADGES" panel listing unlocked badges
 * (spelled-out name + description) plus locked ones greyed out. [Esc] returns
 * to the title screen. When the server reports { enabled: false } a disabled
 * message is shown instead.
 */
import { type CSSProperties, useEffect, useState } from 'react';
import { fetchLeaderboard, type LeaderboardResp } from '../../api.js';
import { Spinner, ErrorBanner } from '../../widgets.js';
import { TerminalFrame } from '../components/TerminalFrame.js';
import { KeyBar, type KeyBarItem } from '../components/KeyBar.js';
import { useKeyboard, type KeyHandler } from '../hooks/useKeyboard.js';
import { BADGE_CODES_ORDERED, badgeName, badgeDescription } from '../config.js';

interface Props {
  onBackToTitle: () => void;
}

export function LeaderboardScreen({ onBackToTitle }: Props) {
  const [data, setData] = useState<LeaderboardResp | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchLeaderboard()
      .then((r) => { if (!cancelled) setData(r); })
      .catch(() => { if (!cancelled) setError('Could not load the leaderboard.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const handlers: KeyHandler[] = [
    { key: 'Escape', handler: onBackToTitle },
  ];
  useKeyboard(handlers);

  const keybar: KeyBarItem[] = [
    { label: 'Esc', description: 'back to title', onActivate: onBackToTitle },
  ];

  const selfName = data?.self?.name ?? null;
  const selfBadges = new Set(data?.self?.badges ?? []);

  return (
    <TerminalFrame
      title={<>ECONQUEST &nbsp;&nbsp;─&nbsp;&nbsp;LEADERBOARD</>}
      keybar={<KeyBar items={keybar} />}
    >
      <div style={{ maxWidth: 720, margin: '0 auto', paddingTop: 16, color: '#cdd5e0' }}>
        {loading && <div style={{ textAlign: 'center', padding: 32 }}><Spinner size={20} /></div>}
        {error && <ErrorBanner msg={error} />}

        {data && data.enabled === false && (
          <div style={{ textAlign: 'center', padding: 32, color: '#97a3b6' }}>
            The leaderboard is currently disabled.
          </div>
        )}

        {data && data.enabled !== false && (
          <>
            {/* Standings table */}
            <div style={{
              padding: 16,
              background: '#0f141c',
              border: '1px solid #1f2937',
              borderRadius: 6,
            }}>
              <div style={{
                fontSize: 11, textTransform: 'uppercase', letterSpacing: 1,
                color: '#97a3b6', marginBottom: 12,
              }}>Top Players</div>

              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ color: '#97a3b6', textAlign: 'left' }}>
                    <th style={thStyle}>RANK</th>
                    <th style={thStyle}>NAME</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>LVL</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>XP</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>BADGES</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.entries ?? []).map((e) => {
                    const isSelf = selfName !== null && e.rank === data.self?.rank && e.name === selfName;
                    return (
                      <tr key={`${e.rank}-${e.name}`} style={{ color: isSelf ? '#a7e8c5' : '#e6ebf2' }}>
                        <td style={tdStyle}>{e.rank}</td>
                        <td style={tdStyle}>{e.name}{isSelf ? ' (you)' : ''}</td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>{e.level}</td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>{e.totalXp}</td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>{e.badges.length}</td>
                      </tr>
                    );
                  })}
                  {(data.entries ?? []).length === 0 && (
                    <tr><td style={tdStyle} colSpan={5}>No ranked players yet — be the first!</td></tr>
                  )}
                </tbody>
              </table>

              {/* Self row when outside the visible top-N */}
              {data.self && !(data.entries ?? []).some((e) => e.rank === data.self?.rank && e.name === selfName) && (
                <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px dashed #1f2937' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <tbody>
                      <tr style={{ color: '#a7e8c5' }}>
                        <td style={tdStyle}>{data.self.rank}</td>
                        <td style={tdStyle}>{data.self.name} (you)</td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>{data.self.level}</td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>{data.self.totalXp}</td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>{data.self.badges.length}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}

              {data.self && (
                <div style={{ marginTop: 8, fontSize: 12, color: '#97a3b6' }}>
                  Rank {data.self.rank} of {data.self.totalPlayers} ranked players.
                </div>
              )}
            </div>

            {/* MY BADGES panel */}
            <div style={{
              marginTop: 16, padding: 16,
              background: '#0f141c', border: '1px solid #1f2937', borderRadius: 6,
            }}>
              <div style={{
                fontSize: 11, textTransform: 'uppercase', letterSpacing: 1,
                color: '#97a3b6', marginBottom: 12,
              }}>My Badges ({selfBadges.size}/{BADGE_CODES_ORDERED.length})</div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                {BADGE_CODES_ORDERED.map((code) => {
                  const unlocked = selfBadges.has(code);
                  return (
                    <div key={code} style={{
                      padding: '6px 10px',
                      borderRadius: 4,
                      border: `1px solid ${unlocked ? '#234' : '#181d27'}`,
                      background: unlocked ? '#101826' : '#0c1016',
                      color: unlocked ? '#e6ebf2' : '#4a5568',
                    }}>
                      <div style={{ fontSize: 13 }}>
                        {unlocked ? '★ ' : '☆ '}{badgeName(code)}
                      </div>
                      <div style={{ fontSize: 11, color: unlocked ? '#97a3b6' : '#3a4452' }}>
                        {unlocked ? badgeDescription(code) : 'Locked'}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}
      </div>
    </TerminalFrame>
  );
}

const thStyle: CSSProperties = {
  padding: '4px 8px',
  borderBottom: '1px solid #1f2937',
  fontSize: 11,
  letterSpacing: 1,
  fontWeight: 'normal',
};

const tdStyle: CSSProperties = {
  padding: '4px 8px',
};
